import { useState, useEffect } from 'react'
import { useNavigate, useSearch } from '@tanstack/react-router'
import { motion } from 'framer-motion'
import { CheckCircle, Users, Gift, ArrowRight, Sparkles } from 'lucide-react'
import { Card, CardHeader, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { useTranslation } from '@/hooks/useTranslation'
import { ParticleNetwork } from '@/components/ui/particle-network'

export default function AffiliationWelcomePage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const search = useSearch({ from: '/ref' })
  const [refCode, setRefCode] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    const code = (search as { code?: string }).code
    if (code) {
      setRefCode(code)
    } else {
      // Redirect to home if no ref code
      navigate({ to: '/dashboard/dash' })
    }
  }, [search, navigate])

  const handleContinueToRegister = async () => {
    setIsLoading(true)
    // Simulate API call to validate ref code
    await new Promise(resolve => setTimeout(resolve, 800))
    
    // Navigate to register page with ref code
    navigate({ to: '/register', search: { ref: refCode } })
    setIsLoading(false)
  }

  const benefits = [
    {
      icon: Gift,
      title: 'Welcome Bonus',
      description: 'Get instant rewards when you complete your registration'
    },
    {
      icon: Users,
      title: 'Exclusive Community',
      description: 'Join a network of successful affiliates and partners'
    },
    {
      icon: Sparkles,
      title: 'Premium Features',
      description: 'Access advanced tools and priority support'
    }
  ]

  if (!refCode) {
    return (
      <div className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <ParticleNetwork className="w-full h-full" />
        </div>
        <div className="absolute inset-0 bg-[#080c15]/60 z-[1]" />
        
        <div className="relative z-10">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-[#0dcbfc]"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="relative min-h-screen overflow-hidden">
      {/* Particle Network Background */}
      <div className="absolute inset-0 z-0">
        <ParticleNetwork className="w-full h-full" />
      </div>

      {/* Dark overlay for better contrast */}
      <div className="absolute inset-0 bg-[#080c15]/60 z-[1]" />

      <div className="relative z-10 container mx-auto px-4 py-8 min-h-screen flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="max-w-4xl mx-auto"
        >
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center justify-center w-16 h-16 bg-[#0dcbfc]/10 rounded-full mb-6 border border-[#0dcbfc]/20">
              <CheckCircle className="w-8 h-8 text-[#0dcbfc]" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-[#0dcbfc] to-[#0dcbfc]/70 bg-clip-text text-transparent">
              Welcome to Nexa!
            </h1>
            <p className="text-xl text-[#0dcbfc]/70 mb-4">
              You've been invited by a valued partner
            </p>
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#0dcbfc]/10 rounded-full border border-[#0dcbfc]/20">
              <span className="text-sm text-[#0dcbfc]/60">Referral Code:</span>
              <code className="text-sm font-mono font-semibold text-[#0dcbfc]">{refCode}</code>
            </div>
          </motion.div>

          {/* Main Content */}
          <div className="grid md:grid-cols-2 gap-8 items-start">
            {/* Welcome Message */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <Card className="bg-[#0a0f1c]/90 border-[#0dcbfc]/20 backdrop-blur-md shadow-[0_0_50px_-12px_rgba(13,203,252,0.25)]">
                <CardHeader>
                  <h2 className="text-2xl font-semibold text-white">You're Invited!</h2>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-[#0dcbfc]/70 leading-relaxed">
                    Congratulations! Someone you know has invited you to join Nexa, 
                    our exclusive platform for digital services and opportunities.
                  </p>
                  <p className="text-[#0dcbfc]/70 leading-relaxed">
                    By joining through this invitation, you'll unlock special benefits 
                    and become part of a growing community of successful partners.
                  </p>
                  <div className="pt-4">
                    <Button 
                      onClick={handleContinueToRegister}
                      disabled={isLoading}
                      size="lg"
                      className="w-full group bg-[#0dcbfc] hover:bg-[#0dcbfc]/90 text-[#080c15] font-semibold shadow-[0_0_20px_-5px_rgba(13,203,252,0.4)] hover:shadow-[0_0_30px_-5px_rgba(13,203,252,0.6)] transition-all"
                    >
                      {isLoading ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#080c15] mr-2" />
                          {t('pages.affiliateWelcome.validating')}
                        </>
                      ) : (
                        <>
                          {t('pages.affiliateWelcome.continueToRegistration')}
                          <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                        </>
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* Benefits */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="space-y-4"
            >
              <h3 className="text-xl font-semibold mb-6 text-white">{t('pages.affiliateWelcome.whatYoullGet')}</h3>
              
              {benefits.map((benefit, index) => (
                <motion.div
                  key={benefit.title}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.4 + (index * 0.1) }}
                >
                  <Card className="bg-[#0a0f1c]/80 border-[#0dcbfc]/20 backdrop-blur-md hover:shadow-[0_0_30px_-5px_rgba(13,203,252,0.3)] transition-all duration-300 hover:-translate-y-1">
                    <CardContent className="p-4">
                      <div className="flex items-start gap-4">
                        <div className="flex-shrink-0">
                          <div className="w-10 h-10 bg-[#0dcbfc]/10 rounded-lg flex items-center justify-center border border-[#0dcbfc]/20">
                            <benefit.icon className="w-5 h-5 text-[#0dcbfc]" />
                          </div>
                        </div>
                        <div>
                          <h4 className="font-semibold mb-1 text-white">{benefit.title}</h4>
                          <p className="text-sm text-[#0dcbfc]/60 leading-relaxed">
                            {benefit.description}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </motion.div>
          </div>

          {/* Footer */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.8 }}
            className="text-center mt-12 pt-8 border-t border-[#0dcbfc]/20"
          >
            <p className="text-sm text-[#0dcbfc]/60">
              {t('pages.affiliateWelcome.alreadyHaveAccount')}{' '}
              <button
                onClick={() => navigate({ to: '/login' })}
                className="text-[#0dcbfc] hover:text-[#0dcbfc]/80 font-medium hover:underline"
              >
                {t('pages.affiliateWelcome.signInHere')}
              </button>
            </p>
          </motion.div>
        </motion.div>
      </div>
    </div>
  )
}
